/**
 * Re-initialize the Facebook Pixel with Advanced Matching once the authenticated
 * user is known. This dramatically improves event match rates in Ads Manager
 * (typically 10% → 30-40%) by giving Facebook hashed email/phone to attribute
 * events to a specific Facebook user.
 *
 * The initial `fbq('init', PIXEL_ID)` happens in src/pages/_document.tsx, before
 * we know who the user is. Calling `fbq('init', PIXEL_ID, userData)` again is
 * idempotent and enriches all subsequent events (including PageView on later
 * navigations).
 *
 * Facebook's pixel SDK (2.9.126+) hashes em/ph client-side automatically.
 *
 * Docs: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export type AdvancedMatchingData = {
  email?: string;
  phone?: string;
};

/**
 * Re-init the pixel with Advanced Matching parameters. Safe to call multiple
 * times; Facebook handles re-init idempotently.
 */
export function identifyFacebookPixel({ email, phone }: AdvancedMatchingData): void {
  if (typeof window === 'undefined' || !window.fbq) return;

  const pixelId = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
  if (!pixelId) return;

  const userData: Record<string, string> = {};
  if (email) userData.em = email;
  if (phone) userData.ph = phone;
  if (Object.keys(userData).length === 0) return;

  try {
    window.fbq('init', pixelId, userData);
  } catch {
    // Silent fail
  }
}
